import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Heart, ShieldCheck, MapPin, MessageCircle, Sparkles, UserPlus, CalendarDays, Clock, Star, Flame, HeartCrack, User2, UserX, Users, Gem, HeartHandshake, MoonStar } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import InlineAdBanner from "@/components/InlineAdBanner";
import AppCategoryChips from "./AppCategoryChips";

const APP_ID = "soulmates";
const THEME = { primary: "#a855f7", accent: "#ec4899" };

const CHIPS = [
  { id: "never-married", label: "Never Married", icon: HeartHandshake, color: "#831843", bg: "#fbcfe8",
    params: { marital_status: "Never Married" } },
  { id: "manglik",       label: "Manglik",       icon: Flame,          color: "#7f1d1d", bg: "#fecaca",
    params: { manglik: "true" } },
  { id: "non-manglik",   label: "Non-Manglik",   icon: MoonStar,       color: "#1e3a8a", bg: "#bfdbfe",
    params: { manglik: "false" } },
  { id: "widow",         label: "Widow",         icon: HeartCrack,     color: "#7c2d12", bg: "#fed7aa",
    params: { marital_status: "Widow" } },
  { id: "widower",       label: "Widower",       icon: UserX,          color: "#78350f", bg: "#fef3c7",
    params: { marital_status: "Widower" } },
  { id: "divorced",      label: "Divorced",      icon: User2,          color: "#155e75", bg: "#a5f3fc" ,
    params: { marital_status: "Divorced" } },
  { id: "aged-30",       label: "Aged 30+",      icon: Users,          color: "#166534", bg: "#bbf7d0",
    params: { age_min: "30" } },
  { id: "aged-40",       label: "Aged 40+",      icon: Users,          color: "#14532d", bg: "#dcfce7",
    params: { age_min: "40" } },
  { id: "high-income",   label: "High Income",   icon: Gem,            color: "#7c2d12", bg: "#fde68a",
    params: { income_min: "25|40" } },
  { id: "verified",      label: "Verified",      icon: ShieldCheck,    color: "#4c1d95", bg: "#ddd6fe",
    params: { via_vendor: "1" } },
];

function ProfileForm({ initial = {}, onSubmit, busy, requireConsent = true }) {
  const [form, setForm] = useState({
    name: "", gender: "female", age: 25, city: "", gotra: "", sub_caste: "",
    education: "", profession: "", height_cm: 165, income_range: "",
    diet: "Pure Veg", photo_url: "", bio: "", marital_status: "Never Married", consent: false,
    ...initial,
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target?.value ?? e }));
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      <Input data-testid="mp-name" placeholder="Full name" required value={form.name} onChange={set("name")} />
      <Select value={form.gender} onValueChange={(v) => setForm((s) => ({ ...s, gender: v }))}>
        <SelectTrigger data-testid="mp-gender"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="female">Female</SelectItem>
          <SelectItem value="male">Male</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>
      <Input data-testid="mp-age" type="number" placeholder="Age" value={form.age} onChange={(e) => setForm((s) => ({ ...s, age: Number(e.target.value) }))} />
      <Input data-testid="mp-city" placeholder="City" value={form.city} onChange={set("city")} />
      <Input data-testid="mp-gotra" placeholder="Gotra" value={form.gotra} onChange={set("gotra")} />
      <Input data-testid="mp-subcaste" placeholder="Sub-caste / Pothi" value={form.sub_caste} onChange={set("sub_caste")} />
      <Input data-testid="mp-education" placeholder="Education" value={form.education} onChange={set("education")} />
      <Input data-testid="mp-profession" placeholder="Profession" value={form.profession} onChange={set("profession")} />
      <Input data-testid="mp-height" type="number" placeholder="Height (cm)" value={form.height_cm} onChange={(e) => setForm((s) => ({ ...s, height_cm: Number(e.target.value) }))} />
      <Input data-testid="mp-income" placeholder="Income range (₹/yr)" value={form.income_range} onChange={set("income_range")} />
      <Input data-testid="mp-diet" placeholder="Diet" value={form.diet} onChange={set("diet")} />
      <Select value={form.marital_status || "Never Married"} onValueChange={(v) => setForm((s) => ({ ...s, marital_status: v }))}>
        <SelectTrigger data-testid="mp-marital-status"><SelectValue placeholder="Marital status" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="Never Married">Never Married</SelectItem>
          <SelectItem value="Widow">Widow</SelectItem>
          <SelectItem value="Widower">Widower</SelectItem>
          <SelectItem value="Divorced">Divorced</SelectItem>
        </SelectContent>
      </Select>
      <Input data-testid="mp-photo" placeholder="Photo URL" value={form.photo_url} onChange={set("photo_url")} />
      <Textarea data-testid="mp-bio" className="sm:col-span-2" rows={2} placeholder="Short bio" value={form.bio} onChange={set("bio")} />
      {requireConsent && (
        <label className="sm:col-span-2 flex items-start gap-2 cursor-pointer text-sm rounded-xl bg-purple-50 dark:bg-purple-950/20 p-3 border border-purple-200/60">
          <Checkbox
            checked={form.consent}
            onCheckedChange={(v) => setForm((s) => ({ ...s, consent: !!v }))}
            data-testid="mp-consent"
            className="mt-0.5"
          />
          <span>
            <strong>Consent:</strong> I agree to publish my matrimonial profile on the
            Soul Mates app and confirm the details above are mine and accurate. I
            understand I can delete this profile any time.
          </span>
        </label>
      )}
      <Button
        type="submit"
        disabled={busy || (requireConsent && !form.consent) || !form.name}
        className="sm:col-span-2 rounded-full"
        style={{ background: THEME.primary }}
        data-testid="mp-submit"
      >
        {busy ? "Saving…" : "Publish profile"}
      </Button>
    </form>
  );
}

export default function SoulMates() {
  const { t } = useLang();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const gender = params.get("gender") || "";
  const search = params.get("search") || "";
  const chip = params.get("chip") || "";
  const [searchDraft, setSearchDraft] = useState(search);
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [mine, setMine] = useState(undefined);
  const [access, setAccess] = useState(undefined); // {allowed, reason, profile_id?}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const q = { page_size: 60 };
    if (city) q.city = city;
    if (gender) q.gender = gender;
    if (search) q.search = search;
    if (chip) {
      const chipCfg = CHIPS.find((c) => c.id === chip);
      if (chipCfg?.params) Object.assign(q, chipCfg.params);
    }
    api.get("/matrimonial/profiles", { params: q })
      .then((r) => {
        let items = r.data?.items || [];
        // Client-side refinement for "verified" (via matchmaker) — the API
        // doesn't have a dedicated flag for this, so we filter on the
        // response.
        if (chip === "verified") {
          items = items.filter((p) => p.owner_vendor_id);
        }
        setProfiles(items);
        setTotal(chip === "verified" ? items.length : (r.data?.total || 0));
      });
  };
  useEffect(load, [city, gender, search, chip]);

  useEffect(() => {
    if (!user) return;
    api.get("/matrimonial/mine").then((r) => setMine(r.data)).catch(() => setMine(null));
    api.get("/matrimonial/access").then((r) => setAccess(r.data)).catch(() => setAccess({ allowed: false, reason: "unknown" }));
  }, [user]);

  const updateParam = (key, val) => {
    const np = new URLSearchParams(params);
    if (!val) np.delete(key); else np.set(key, val);
    setParams(np);
  };

  const submitMine = async (form) => {
    setBusy(true);
    try {
      const { data } = await api.post("/matrimonial/profiles", form);
      setMine(data);
      setOpen(false);
      toast.success("Application submitted — awaiting superadmin approval");
      // Refresh access; it likely stays pending until approved
      api.get("/matrimonial/access").then((r) => setAccess(r.data)).catch(() => {});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "profile_already_exists" ? "You already have a profile" : "Could not create profile");
    } finally {
      setBusy(false);
    }
  };

  const deleteMine = async () => {
    if (!mine?.profile_id) return;
    if (!window.confirm("Remove your matrimonial profile from Soul Mates?")) return;
    await api.delete(`/matrimonial/profiles/${mine.profile_id}`);
    setMine(null);
    toast.success("Profile removed");
    load();
  };

  return (
    <div className="min-h-screen">
      {access && !access.allowed && (
        <div className="mx-auto max-w-screen-lg px-4 sm:px-6 lg:px-8 py-16" data-testid="app-soulmates-access-denied">
          <Card className="p-8 border-purple-200/60 bg-purple-50/40 dark:bg-purple-950/10">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-purple-700 dark:text-purple-300"><Heart className="h-3 w-3" /> Soul Mates</div>
            <h1 className="font-heading text-3xl tracking-tighter mt-2">Members-only community</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg">
              Soul Mates is opt-in and reserved for approved matrimonial candidates and their matchmakers.
              {access.reason === "pending_approval" && " Your application is under review by our superadmins — you'll get access as soon as it's approved."}
              {access.reason === "not_applied" && " Apply below with your details; a superadmin reviews every submission before your listing goes live."}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {access.reason !== "pending_approval" && (
                <Button className="rounded-full" style={{ background: THEME.primary, color: "#fff" }} onClick={() => setOpen(true)} data-testid="app-soulmates-apply-btn">
                  <UserPlus className="h-4 w-4 mr-1" /> Apply for matrimonial listing
                </Button>
              )}
              {access.reason === "pending_approval" && (
                <Badge className="bg-amber-500 text-white border-0 px-3 py-1.5 rounded-full">Awaiting superadmin approval</Badge>
              )}
              <Button asChild variant="outline" className="rounded-full">
                <a href="/apps">Back to apps</a>
              </Button>
            </div>
          </Card>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Apply for a matrimonial listing</DialogTitle>
                <DialogDescription>
                  Fill in your details. A superadmin will review and approve before your profile goes live.
                </DialogDescription>
              </DialogHeader>
              <ProfileForm onSubmit={submitMine} busy={busy} />
            </DialogContent>
          </Dialog>
        </div>
      )}
      {access && access.allowed && (
      <>
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#4a044e 0%, #86198f 40%, #ec4899 100%)" }}
        data-testid="app-soulmates-hero"
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(40,8,40,0.4), rgba(40,8,40,0.05))" }} />
        <div className="relative mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-16 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-medium">
            Soul Mates · Brahmin matrimonial within the community
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t("apps.soulmates.name")}</h1>
          <p className="mt-2 max-w-xl text-sm sm:text-base opacity-90">Member-listed candidates &amp; trusted matchmakers — opt-in with consent. {t("apps.soulmates.tagline")}</p>

          <div className="mt-6 max-w-2xl bg-white rounded-2xl p-2 flex items-center gap-2 shadow-2xl">
            <Input
              placeholder="Search: name, education, profession…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && updateParam("search", searchDraft)}
              className="border-0 focus-visible:ring-0 text-foreground h-11"
              data-testid="app-soulmates-search"
            />
            <Select value={gender || "all"} onValueChange={(v) => updateParam("gender", v === "all" ? "" : v)}>
              <SelectTrigger className="h-11 w-32 border-0 text-foreground bg-muted" data-testid="app-soulmates-gender">
                <SelectValue placeholder="Looking for" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="female">Bride</SelectItem>
                <SelectItem value="male">Groom</SelectItem>
              </SelectContent>
            </Select>
            <Button className="rounded-xl h-11 px-5 text-white" style={{ background: THEME.primary }} onClick={() => updateParam("search", searchDraft)} data-testid="app-soulmates-search-btn">
              Find matches
            </Button>
          </div>
          <AppCategoryChips
            chips={CHIPS}
            active={chip}
            onChange={(id) => updateParam("chip", id || "")}
            testIdPrefix="app-soulmates"
          />
        </div>
      </section>

      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 pt-6" data-testid="app-soulmates-adslot">
        <InlineAdBanner slot={`app-${APP_ID}`} city={city} />
      </div>

      <section className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-8">
        {/* My-profile banner */}
        <Card className="p-4 border-border/60 mb-6 flex items-center gap-3 flex-wrap" data-testid="app-soulmates-my-profile">
          <div className="h-10 w-10 rounded-xl bg-pink-100 text-pink-700 flex items-center justify-center"><Heart className="h-5 w-5" /></div>
          {mine === undefined && <span className="text-sm text-muted-foreground">…</span>}
          {mine === null && (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Looking for a partner?</div>
                <div className="text-xs text-muted-foreground">Publish your own matrimonial profile (with consent). You can delete it any time.</div>
              </div>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full text-white" style={{ background: THEME.primary }} data-testid="app-soulmates-create-mine-btn">
                    <UserPlus className="h-4 w-4 mr-1" /> Create my profile
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Create your matrimonial profile</DialogTitle></DialogHeader>
                  <ProfileForm onSubmit={submitMine} busy={busy} />
                </DialogContent>
              </Dialog>
            </>
          )}
          {mine && (
            <>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">Your profile is live</div>
                <div className="text-xs text-muted-foreground">Listed as {mine.name} · {mine.city || "—"} · {mine.gender} · {mine.age}</div>
              </div>
              <Button variant="outline" className="rounded-full" onClick={deleteMine} data-testid="app-soulmates-delete-mine">Remove</Button>
            </>
          )}
        </Card>

        <div className="mb-4 text-sm text-muted-foreground" data-testid="app-soulmates-count">{total} profiles</div>
        {profiles.length === 0 ? (
          <Card className="p-10 border-border/60 text-center" data-testid="app-soulmates-empty">
            <p className="text-muted-foreground">No matrimonial profiles yet. Be the first to publish yours.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="app-soulmates-grid">
            {profiles.map((p, idx) => (
              <React.Fragment key={p.profile_id}>
                {idx > 0 && idx % 8 === 0 && (
                  <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4" data-testid={`app-soulmates-grid-ad-${idx}`}>
                    <InlineAdBanner slot={`app-${APP_ID}`} city={city} />
                  </div>
                )}
                <Card className="overflow-hidden border-border/60 flex flex-col" data-testid={`app-soulmates-card-${p.profile_id}`}>
                  <div className="aspect-[3/4] w-full bg-muted relative">
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-purple-300">
                        <Sparkles className="h-12 w-12" />
                      </div>
                    )}
                    <Badge className="absolute top-2 left-2 bg-white/90 text-purple-900 border-0">{p.age} · {p.gender === "female" ? "F" : p.gender === "male" ? "M" : p.gender}</Badge>
                    {p.kundali?.manglik && (
                      <Badge className="absolute top-2 right-2 bg-rose-600 text-white border-0 gap-1" data-testid={`app-soulmates-manglik-${p.profile_id}`}>
                        <Star className="h-3 w-3" /> Manglik
                      </Badge>
                    )}
                    {p.owner_label && p.owner_label !== "Self-listed" && (
                      <Badge className="absolute bottom-2 left-2 bg-purple-900/85 text-white border-0 gap-1"><ShieldCheck className="h-3 w-3" /> via {p.owner_label}</Badge>
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="font-semibold text-base leading-tight truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {p.city || "—"} · {p.gotra || "Gotra n/a"}</div>
                    {p.marital_status && p.marital_status !== "Never Married" && (
                      <Badge variant="outline" className="rounded-full text-[10px] mt-1 w-fit" data-testid={`app-soulmates-marital-${p.profile_id}`}>{p.marital_status}</Badge>
                    )}
                    {(p.education || p.profession) && (
                      <div className="text-xs text-muted-foreground mt-1 truncate">{[p.education, p.profession].filter(Boolean).join(" · ")}</div>
                    )}
                    {(p.birth_date || p.birth_time) && (
                      <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap" data-testid={`app-soulmates-birth-${p.profile_id}`}>
                        {p.birth_date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {p.birth_date}</span>}
                        {p.birth_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.birth_time}</span>}
                      </div>
                    )}
                    {p.kundali && (p.kundali.rashi || p.kundali.nakshatra) && (
                      <div className="text-[11px] mt-1 rounded-md bg-purple-50 dark:bg-purple-950/20 px-2 py-1 text-purple-800 dark:text-purple-200 truncate" data-testid={`app-soulmates-kundali-${p.profile_id}`} title={`Rashi: ${p.kundali.rashi || "—"} · Nakshatra: ${p.kundali.nakshatra || "—"} · Gana: ${p.kundali.gana || "—"}`}>
                        🪔 {p.kundali.rashi || "—"} · {p.kundali.nakshatra || "—"}
                      </div>
                    )}
                    {p.bio && <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{p.bio}</p>}
                    <Button asChild size="sm" className="rounded-full mt-auto" style={{ background: THEME.primary }} data-testid={`app-soulmates-connect-${p.profile_id}`}>
                      {p.owner_vendor_id ? (
                        <Link to={`/vendors/${p.owner_vendor_id}?chat=1`}><MessageCircle className="h-3 w-3 mr-1" /> Connect via matchmaker</Link>
                      ) : (
                        <a href="#" onClick={(e) => { e.preventDefault(); toast.info("Direct messaging members is coming soon"); }}>
                          <MessageCircle className="h-3 w-3 mr-1" /> Express interest
                        </a>
                      )}
                    </Button>
                  </div>
                </Card>
              </React.Fragment>
            ))}
          </div>
        )}
      </section>
      </>
      )}
    </div>
  );
}
